import React, { useState, useEffect } from 'react';
import { getBundles, createBundle, createApp } from '../../services/api';

function BundleManager() {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAppForm, setShowAppForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [creatingApp, setCreatingApp] = useState(false);
  const [newBundle, setNewBundle] = useState({
    name: '',
    identifier: '',
    platform: 'IOS'
  });
  const [newApp, setNewApp] = useState({
    bundleId: '',
    name: '',
    sku: '',
    primaryLocale: 'en-US'
  });

  useEffect(() => {
    loadBundles();
  }, []);

  // Reload bundles when app form is opened
  useEffect(() => {
    if (showAppForm) {
      console.log('App form opened, reloading bundles...');
      loadBundles();
    }
  }, [showAppForm]);

  const loadBundles = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getBundles();
      //console.log('Bundle IDs response:', response.data);
      if (response.data.success) {
        const bundleData = response.data.bundles || response.data.bundleIds || [];
        //console.log('Bundle IDs loaded:', bundleData);
        setBundles(bundleData);
      } else {
        const errorMsg = typeof response.data.error === 'object' 
          ? response.data.error.message || 'Failed to load bundle IDs'
          : response.data.error || 'Failed to load bundle IDs';
        setError(errorMsg);
      }
    } catch (err) {
      //console.error('Error loading bundles:', err);
      const errorMsg = typeof err.response?.data?.error === 'object'
        ? err.response.data.error.message || 'Failed to load bundle IDs'
        : err.response?.data?.error || err.message || 'Failed to load bundle IDs';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBundle = async (e) => {
    e.preventDefault();
    
    if (!newBundle.name || !newBundle.identifier) {
      alert('Please fill in all required fields');
      return;
    }

    // Validate bundle ID format (reverse domain notation)
    const bundleIdPattern = /^[a-zA-Z0-9.-]+$/;
    if (!bundleIdPattern.test(newBundle.identifier)) {
      alert('Invalid Bundle ID format. Use reverse domain notation (e.g., com.company.appname)');
      return;
    }

    setCreating(true);
    setError('');

    try {
      const response = await createBundle(newBundle);
      if (response.data.success) {
        alert('Bundle ID created successfully!');
        setNewBundle({ name: '', identifier: '', platform: 'IOS' });
        setShowCreateForm(false);
        await loadBundles(); // Reload the list
      } else {
        const errorMsg = typeof response.data.error === 'object' 
          ? response.data.error.message || 'Failed to create bundle ID'
          : response.data.error || 'Failed to create bundle ID';
        alert(errorMsg);
      }
    } catch (err) {
      const errorMsg = typeof err.response?.data?.error === 'object'
        ? err.response.data.error.message || 'Failed to create bundle ID'
        : err.response?.data?.error || err.message || 'Failed to create bundle ID';
      alert(errorMsg);
    } finally {
      setCreating(false);
    }
  };

  const handleCreateApp = async (e) => {
    e.preventDefault();
    
    if (!newApp.bundleId || !newApp.name || !newApp.sku) {
      alert('Please fill in all required fields');
      return;
    }

    // Validate SKU format (alphanumeric, no spaces)
    const skuPattern = /^[a-zA-Z0-9_-]+$/;
    if (!skuPattern.test(newApp.sku)) {
      alert('Invalid SKU format. Use alphanumeric characters, hyphens, and underscores only (no spaces)');
      return;
    }

    setCreatingApp(true);
    setError('');

    try {
      const response = await createApp(newApp);
      if (response.data.success) {
        alert(`App "${newApp.name}" created successfully!\n\nYou can now manage it in the App Store Management section.`);
        setNewApp({ bundleId: '', name: '', sku: '', primaryLocale: 'en-US' });
        setShowAppForm(false);
      } else {
        const errorMsg = typeof response.data.error === 'object' 
          ? response.data.error.message || 'Failed to create app'
          : response.data.error || 'Failed to create app';
        alert(errorMsg);
      }
    } catch (err) {
      const errorMsg = typeof err.response?.data?.error === 'object'
        ? err.response.data.error.message || 'Failed to create app'
        : err.response?.data?.error || err.message || 'Failed to create app';
      alert(errorMsg);
    } finally {
      setCreatingApp(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-inline">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <span className="alert-icon">⚠️</span>
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <p className="text-secondary" style={{ margin: 0 }}>
          Bundle IDs uniquely identify your app on the App Store and in the iOS ecosystem.
        </p>
        <div style={{ display: 'flex', gap: '8px' }}>
          {!showCreateForm && !showAppForm && (
            <>
              <button
                className="btn btn-success"
                onClick={() => setShowAppForm(true)}
              >
                ➕ Create App
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setShowCreateForm(true)}
              >
                ➕ Create Bundle ID
              </button>
            </>
          )}
        </div>
      </div>

      {showCreateForm && (
        <div className="form-card" style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px', fontSize: '1.1rem' }}>Create New Bundle ID</h3>
          
          <form onSubmit={handleCreateBundle}>
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., My Awesome App"
                value={newBundle.name}
                onChange={(e) => setNewBundle({ ...newBundle, name: e.target.value })}
                required
              />
              <small className="text-secondary">
                A descriptive name for your app
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">Bundle ID (Identifier) *</label>
              <input
                type="text"
                className="form-input mono"
                placeholder="com.company.appname"
                value={newBundle.identifier}
                onChange={(e) => setNewBundle({ ...newBundle, identifier: e.target.value.toLowerCase() })}
                required
              />
              <small className="text-secondary">
                Use reverse domain notation. Must be unique. Cannot be changed later.
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">Platform *</label>
              <select
                className="form-select"
                value={newBundle.platform}
                onChange={(e) => setNewBundle({ ...newBundle, platform: e.target.value })}
              >
                <option value="IOS">iOS</option>
                <option value="MAC_OS">macOS</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewBundle({ name: '', identifier: '', platform: 'IOS' });
                }}
                disabled={creating}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={creating}
                style={{ flex: 1 }}
              >
                {creating ? 'Creating...' : 'Create Bundle ID'}
              </button>
            </div>
          </form>
        </div>
      )}

      {showAppForm && (
        <div className="form-card form-card-success" style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '16px', fontSize: '1.1rem' }}>
            🚀 Create New App
          </h3>
          
          <div className="alert alert-warning" style={{ marginBottom: '16px' }}>
            <span className="alert-icon">⚠️</span>
            <span>
              <strong>Note:</strong> Apple has restricted app creation via API for most accounts. 
              If you get a permissions error, you'll need to create the app manually in 
              <a href="https://appstoreconnect.apple.com" target="_blank" rel="noopener noreferrer" className="text-primary" style={{ marginLeft: '4px' }}>
                App Store Connect
              </a>, then manage it here.
            </span>
          </div>
          
          <form onSubmit={handleCreateApp}>
            <div className="form-group">
              <label className="form-label">App Name *</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., My Awesome App"
                value={newApp.name}
                onChange={(e) => setNewApp({ ...newApp, name: e.target.value })}
                required
                maxLength={30}
              />
              <small className="text-secondary">
                The name of your app as it will appear on the App Store (max 30 characters)
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">SKU (Stock Keeping Unit) *</label>
              <input
                type="text"
                className="form-input mono"
                placeholder="e.g., my-awesome-app-001"
                value={newApp.sku}
                onChange={(e) => setNewApp({ ...newApp, sku: e.target.value })}
                required
              />
              <small className="text-secondary">
                A unique identifier for your app (alphanumeric, hyphens, underscores - no spaces)
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">Bundle ID *</label>
              <select
                className="form-select mono"
                value={newApp.bundleId}
                onChange={(e) => setNewApp({ ...newApp, bundleId: e.target.value })}
                required
              >
                <option value="">-- Select a Bundle ID --</option>
                {(() => {
                  //console.log('All bundles:', bundles);
                  const iosBundles = bundles.filter(b => {
                    const platform = b.attributes?.platform || b.platform;
                    //console.log('Bundle platform:', platform, 'for bundle:', b);
                    return platform === 'IOS' || platform === 'iOS';
                  });
                  //console.log('Filtered iOS bundles:', iosBundles);
                  
                  if (iosBundles.length === 0 && bundles.length > 0) {
                    // If no iOS bundles found but bundles exist, show all bundles
                    //console.warn('No iOS bundles found, showing all bundles');
                    return bundles.map(bundle => (
                      <option 
                        key={bundle.id} 
                        value={bundle.id}
                      >
                        {bundle.attributes?.identifier || bundle.identifier} 
                        ({bundle.attributes?.name || bundle.name})
                      </option>
                    ));
                  }
                  
                  return iosBundles.map(bundle => (
                    <option 
                      key={bundle.id} 
                      value={bundle.id}
                    >
                      {bundle.attributes?.identifier || bundle.identifier} 
                      ({bundle.attributes?.name || bundle.name})
                    </option>
                  ));
                })()}
              </select>
              <small className="text-secondary">
                Select an existing Bundle ID to link to this app
                {bundles.length === 0 && ' (No Bundle IDs found - create one first!)'}
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">Primary Language</label>
              <select
                className="form-select"
                value={newApp.primaryLocale}
                onChange={(e) => setNewApp({ ...newApp, primaryLocale: e.target.value })}
              >
                <option value="en-US">English (U.S.)</option>
                <option value="en-GB">English (U.K.)</option>
                <option value="es-ES">Spanish (Spain)</option>
                <option value="es-MX">Spanish (Mexico)</option>
                <option value="fr-FR">French (France)</option>
                <option value="de-DE">German (Germany)</option>
                <option value="it-IT">Italian (Italy)</option>
                <option value="pt-BR">Portuguese (Brazil)</option>
                <option value="pt-PT">Portuguese (Portugal)</option>
                <option value="ja-JP">Japanese (Japan)</option>
                <option value="ko-KR">Korean (Korea)</option>
                <option value="zh-Hans">Chinese (Simplified)</option>
                <option value="zh-Hant">Chinese (Traditional)</option>
                <option value="ru-RU">Russian (Russia)</option>
                <option value="ar-SA">Arabic (Saudi Arabia)</option>
                <option value="hi-IN">Hindi (India)</option>
              </select>
              <small className="text-secondary">
                The primary language for your app's metadata
              </small>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowAppForm(false);
                  setNewApp({ bundleId: '', name: '', sku: '', primaryLocale: 'en-US' });
                }}
                disabled={creatingApp}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-success"
                disabled={creatingApp}
                style={{ flex: 1 }}
              >
                {creatingApp ? 'Creating App...' : 'Create App'}
              </button>
            </div>
          </form>
        </div>
      )}

      {bundles.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📦</div>
          <p className="empty-state-title">No bundle IDs found</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Bundle ID</th>
                <th>Platform</th>
              </tr>
            </thead>
            <tbody>
              {bundles.map(bundle => (
                <tr key={bundle.id}>
                  <td>{bundle.attributes?.name || bundle.name || 'N/A'}</td>
                  <td className="mono text-primary" style={{ fontSize: '13px' }}>
                    {bundle.attributes?.identifier || bundle.identifier || 'N/A'}
                  </td>
                  <td>{bundle.attributes?.platform || bundle.platform || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="alert alert-info" style={{ marginTop: '20px' }}>
        <span className="alert-icon">💡</span>
        <span>
          <strong>Tip:</strong> Use the CLI tool to register new bundle IDs and manage capabilities.
        </span>
      </div>
    </div>
  );
}

export default BundleManager;
